<script setup lang="ts">
const showcaseFeatures = [
  {
    title: "Live Dashboard",
    description: "Monitor all your trades in real-time with instant price updates and P&L calculations",
    image: "/homepage-screen-close.png",
    stats: [
      { label: "Update Speed", value: "<100ms" },
      { label: "Exchanges", value: "10+" },
    ],
  },
  {
    title: "Advanced Analytics",
    description: "Dive deep into your trading performance with comprehensive charts and insights",
    image: "/homepage-screen-white.png",
    stats: [
      { label: "Report Types", value: "20+" },
      { label: "Data Points", value: "1M+" },
    ],
  },
];

const capabilities = [
  {
    icon: "i-heroicons-rocket-launch",
    title: "Lightning Fast",
    description: "Real-time updates under 100ms",
    metric: "99.9% uptime",
  },
  {
    icon: "i-heroicons-shield-check",
    title: "Bank-Grade Security",
    description: "Your data is encrypted and secure",
    metric: "SOC 2 compliant",
  },
  {
    icon: "i-heroicons-globe-alt",
    title: "Global Support",
    description: "Multi-currency and tax calculations",
    metric: "15+ currencies",
  },
  {
    icon: "i-heroicons-cog-6-tooth",
    title: "API Access",
    description: "Integrate with your own tools",
    metric: "REST & WebSocket",
  },
];
</script>

<template>
  <div class="relative py-24 overflow-hidden bg-background sm:py-32">
    <div class="px-6 mx-auto max-w-7xl lg:px-8">
      <!-- Section Header -->
      <div class="max-w-3xl mx-auto mb-20 text-center">
        <div class="inline-flex items-center px-3 py-1 mb-6 text-sm font-medium rounded-full bg-primary/10 text-primary">
          Platform Overview
        </div>
        <h2 class="text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl">
          Built for
          <span class="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            professional traders
          </span>
        </h2>
        <p class="max-w-2xl mx-auto mt-6 text-lg text-muted-foreground">
          Experience the most advanced crypto portfolio management platform with enterprise-grade features and consumer-friendly design.
        </p>
      </div>

      <!-- Showcase Features (Alternating Layout) -->
      <div class="space-y-32">
        <div
          v-for="(feature, index) in showcaseFeatures"
          :key="feature.title"
          :class="[
            'grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16 items-center',
            index % 2 === 1 ? 'lg:flex-row-reverse' : '',
          ]">
          <!-- Text Content -->
          <div :class="index % 2 === 1 ? 'lg:order-2' : ''">
            <h3 class="text-3xl font-bold text-foreground sm:text-4xl">
              {{ feature.title }}
            </h3>
            <p class="mt-4 text-lg leading-8 text-muted-foreground">
              {{ feature.description }}
            </p>

            <!-- Stats -->
            <div class="grid grid-cols-2 gap-6 mt-8">
              <div
                v-for="stat in feature.stats"
                :key="stat.label"
                class="p-4 rounded-lg bg-card border border-border">
                <div class="text-2xl font-bold text-foreground">
                  {{ stat.value }}
                </div>
                <div class="mt-1 text-sm text-muted-foreground">
                  {{ stat.label }}
                </div>
              </div>
            </div>

            <!-- Learn More Link -->
            <a
              href="#features"
              class="inline-flex items-center gap-2 mt-8 text-base font-semibold transition-colors duration-200 text-secondary hover:text-secondary/80 group">
              Explore features
              <UIcon
                name="i-heroicons-arrow-right"
                class="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
          </div>

          <!-- Image with Glass Effect -->
          <div :class="index % 2 === 1 ? 'lg:order-1' : ''">
            <div
              class="relative p-4 rounded-2xl bg-card/40 backdrop-blur-sm border border-border shadow-2xl">
              <LazyNuxtImg
                :src="feature.image"
                :alt="`${feature.title} screenshot`"
                class="w-full rounded-lg shadow-xl"
                loading="lazy" />
              
              <!-- Glow Effect -->
              <div
                :class="[
                  'absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500',
                  index % 2 === 0 ? 'bg-primary/5' : 'bg-secondary/5',
                ]"
                aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>

      <!-- Capabilities Grid (Vercel-inspired) -->
      <div class="mt-32">
        <div class="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div
            v-for="capability in capabilities"
            :key="capability.title"
            class="relative p-6 rounded-xl bg-card border border-border group hover:border-primary/30 transition-all duration-300">
            <!-- Icon -->
            <div
              class="inline-flex items-center justify-center w-12 h-12 mb-4 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20">
              <UIcon
                :name="capability.icon"
                class="w-6 h-6 text-primary" />
            </div>

            <!-- Content -->
            <h3 class="mb-2 text-lg font-semibold text-foreground">
              {{ capability.title }}
            </h3>
            <p class="mb-4 text-sm text-muted-foreground">
              {{ capability.description }}
            </p>

            <!-- Metric Badge -->
            <div
              class="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-muted text-foreground">
              {{ capability.metric }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
